import { baseApi } from './baseApi';
import {
  OrderSplit,
  CreateOrderSplitsRequest,
  ProcessSplitPaymentRequest,
  SplitOperationResult,
  SplitValidationRequest,
  SplitValidationResult,
  MergeOrderSplitsRequest,
} from '../types/splits';

// La división se crea y cancela como unidad (no hay edición por cuenta); el
// descuento por cuenta viaja dentro del pago. Espejo de OrderSplitsController.
export const splitsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create splits for an order
    createOrderSplits: builder.mutation<SplitOperationResult, CreateOrderSplitsRequest>({
      query: (createSplitsRequest) => ({
        url: '/ordersplits',
        method: 'POST',
        body: createSplitsRequest,
      }),
      invalidatesTags: ['Split', 'Order'],
    }),

    // Validate split configuration before creation
    validateOrderSplits: builder.mutation<SplitValidationResult, SplitValidationRequest>({
      query: (validationRequest) => ({
        url: '/ordersplits/validate',
        method: 'POST',
        body: validationRequest,
      }),
    }),

    // Get all splits for an order
    getOrderSplits: builder.query<OrderSplit[], string>({
      query: (orderId) => `/ordersplits/order/${orderId}`,
      providesTags: (result, error, orderId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Split' as const, id })),
              { type: 'Split', id: `order-${orderId}` },
            ]
          : [{ type: 'Split', id: `order-${orderId}` }],
    }),

    // Cancel the whole division for an order (only while no split is paid)
    cancelOrderSplits: builder.mutation<void, string>({
      query: (orderId) => ({
        url: `/ordersplits/order/${orderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Split', 'Order'],
    }),

    // Junta cuentas pendientes en una (una persona paga varias con un solo cobro)
    mergeOrderSplits: builder.mutation<SplitOperationResult, MergeOrderSplitsRequest>({
      query: ({ orderId, splitIds }) => ({
        url: `/ordersplits/order/${orderId}/merge`,
        method: 'POST',
        body: { splitIds },
      }),
      invalidatesTags: ['Split'],
    }),

    // Process payment for a split
    processSplitPayment: builder.mutation<SplitOperationResult, ProcessSplitPaymentRequest>({
      query: ({ splitId, ...paymentData }) => ({
        url: `/ordersplits/${splitId}/payments`,
        method: 'POST',
        body: { splitId, ...paymentData },
      }),
      invalidatesTags: (result, error, { splitId }) => [
        { type: 'Split', id: splitId },
        { type: 'SplitPayment', id: splitId },
        'Split',
        // Paying the last split closes the order and frees the table flow
        'Order',
      ],
    }),

  }),
});

// Export hooks (solo los que la UI consume; el backend expone más endpoints
// —receipt, payment-status, can-split, can-cancel— que hoy no tienen consumidor)
export const {
  useCreateOrderSplitsMutation,
  useValidateOrderSplitsMutation,
  useGetOrderSplitsQuery,
  useCancelOrderSplitsMutation,
  useMergeOrderSplitsMutation,
  useProcessSplitPaymentMutation,
} = splitsApi;
